
import { useState, useEffect } from 'react';

export interface LocationData {
  lat: number;
  lng: number;
}

export const useLocation = () => {
  const [location, setLocation] = useState<LocationData | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);

  const getCurrentLocation = (): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      });
    });
  };

  const requestLocation = async () => {
    setLocationError(null);
    setLocationLoading(true);
    try {
      const position = await getCurrentLocation();
      const locationData = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      setLocation(locationData);
      return locationData;
    } catch (error) {
      const errorMessage = 'Unable to get location. Please enable location services.';
      setLocationError(errorMessage);
      console.error('Location error:', error);
      throw error;
    } finally {
      setLocationLoading(false);
    }
  };

  // Get location on hook initialization
  useEffect(() => {
    requestLocation();
  }, []);

  return {
    location,
    locationError,
    locationLoading,
    requestLocation,
    getCurrentLocation
  };
};
